import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { S25RouterLinkDirective } from "../../s25-nav-bar/s25.router.link.directive";
import { S25States } from "../../../services/state/state.map";
import { S25Util } from "../../../util/s25-util";
import { UserStore } from "../../../stores/user.store";
import type { SeUserTileAttendedEvent, SeUserTileDocument, SeUserTileOrganization } from "./se.user.search.service";

@TypeManagerDecorator("s25-ng-se-user-search-row-detail")
@Component({
    selector: "s25-ng-se-user-search-row-detail",
    standalone: true,
    imports: [CommonModule, S25RouterLinkDirective],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="user-search-row-detail">
            @if (contactTextComment(); as commentBody) {
                <section class="detail-section" aria-labelledby="user-detail-comment-heading">
                    <h3 id="user-detail-comment-heading" class="detail-heading">Contact comment</h3>
                    <div class="contact-comment-body">{{ commentBody }}</div>
                </section>
            }

            <section class="detail-section" aria-labelledby="user-detail-orgs-heading">
                <h3 id="user-detail-orgs-heading" class="detail-heading">Organizations</h3>
                @if (organizations().length) {
                    <ul class="detail-list detail-list--orgs">
                        @for (
                            organizationBlock of organizations();
                            track organizationBlock.organizationId + "|" + organizationBlock.organizationName
                        ) {
                            <li class="org-membership-row">
                                <a
                                    class="connect25-detail-link"
                                    s25-ng-router-link
                                    [stateName]="seOrganizationDetailsStateName"
                                    [stateParams]="organizationDetailLinkParams(organizationBlock)"
                                >
                                    {{ organizationBlock.organizationName }}
                                </a>
                                @if (joinedOrganizationRoleNames(organizationBlock); as rolesJoined) {
                                    <span class="role-sep"> — </span>
                                    <span class="role-name">{{ rolesJoined }}</span>
                                }
                            </li>
                        }
                    </ul>
                } @else {
                    <p class="detail-empty">No organization memberships</p>
                }
            </section>

            <section class="detail-section" aria-labelledby="user-detail-events-heading">
                <h3 id="user-detail-events-heading" class="detail-heading">Events attended</h3>
                @if (events().length) {
                    <ul class="detail-list detail-list--events">
                        @for (attendedEvent of events(); track attendedEvent.eventId) {
                            <li class="event-attended-row">
                                <span class="event-date">{{ formatEventStart(attendedEvent.startDate) }}</span>
                                <a
                                    class="connect25-detail-link"
                                    s25-ng-router-link
                                    [stateName]="studentEngagementEventDetailsStateName"
                                    [stateParams]="eventDetailLinkParams(attendedEvent)"
                                >
                                    {{ attendedEvent.eventName ?? "Event" }}
                                </a>
                            </li>
                        }
                    </ul>
                } @else {
                    <p class="detail-empty">No checked-in Student Engagement events</p>
                }
            </section>
        </div>
    `,
    styles: `
        .user-search-row-detail {
            padding: 0.75rem 1rem 1rem;
            display: flex;
            flex-direction: column;
            gap: 1.25rem;
            max-width: 720px;
        }
        .detail-heading {
            margin: 0 0 0.5rem;
            font-size: 0.95rem;
            font-weight: 600;
            color: var(--color--text-strong);
        }
        .contact-comment-body {
            margin: 0;
            font-size: 0.9rem;
            color: var(--color--text);
            white-space: pre-wrap;
            word-break: break-word;
        }
        .detail-list--orgs {
            list-style: none;
            padding-left: 0;
        }
        .org-membership-row {
            display: flex;
            flex-wrap: wrap;
            align-items: baseline;
            gap: 0.5rem 1rem;
            margin-bottom: 0.5rem;
        }
        .detail-list--events {
            list-style: none;
            padding-left: 0;
        }
        .detail-empty {
            margin: 0;
            color: var(--color--text-muted);
            font-size: 0.9rem;
        }
        .event-attended-row {
            display: flex;
            flex-wrap: wrap;
            align-items: baseline;
            gap: 0.5rem 1rem;
            margin-bottom: 0.5rem;
        }
        .event-date {
            flex: 0 0 auto;
            min-width: 10rem;
            font-size: 0.9rem;
            color: var(--color--text-muted);
        }
        .connect25-detail-link {
            font-weight: 500;
            color: var(--color--link-text, #0c447c);
            text-decoration: none;
        }
        .connect25-detail-link:hover {
            text-decoration: underline;
        }
        .role-name {
            color: var(--color--text-muted);
        }
    `,
})
export class SeUserSearchRowDetailComponent {
    readonly userRow = input.required<SeUserTileDocument>();

    private readonly userStore = inject(UserStore);

    readonly seOrganizationDetailsStateName = S25States.names.seOrganizationDetails;

    readonly studentEngagementEventDetailsStateName = S25States.names.studentEngagementEventDetails;

    readonly contactTextComment = computed(() => {
        const trimmed = this.userRow().textComment?.trim();
        return trimmed?.length ? trimmed : null;
    });

    readonly organizations = computed(() => this.userRow().organizations ?? []);

    readonly events = computed((): SeUserTileAttendedEvent[] => {
        const raw = this.userRow().eventsAttended ?? [];
        return [...raw].sort((firstEvent, secondEvent) => {
            const firstStart = firstEvent.startDate ?? "";
            const secondStart = secondEvent.startDate ?? "";
            if (!firstStart && !secondStart) {
                return 0;
            }
            if (!firstStart) {
                return 1;
            }
            if (!secondStart) {
                return -1;
            }
            return firstStart.localeCompare(secondStart);
        });
    });

    organizationDetailLinkParams(
        organizationBlock: SeUserTileOrganization,
    ): S25States.ParamsForName<"seOrganizationDetails"> {
        return { itemId: organizationBlock.organizationId };
    }

    joinedOrganizationRoleNames(organizationBlock: SeUserTileOrganization): string | null {
        const trimmedRoles = (organizationBlock.roleNames ?? [])
            .map((roleName) => roleName?.trim())
            .filter((trimmed): trimmed is string => !!trimmed?.length);
        return trimmedRoles.length ? trimmedRoles.join(", ") : null;
    }

    eventDetailLinkParams(
        attendedEvent: SeUserTileAttendedEvent,
    ): S25States.ParamsForName<"studentEngagementEventDetails"> {
        return { itemId: attendedEvent.eventId };
    }

    formatEventStart(startDateIso: string | undefined): string {
        if (!startDateIso) {
            return "—";
        }
        const parsed = S25Util.date.parse(startDateIso);
        const format = this.userStore.dateTimeFormat();
        if (!format) {
            return startDateIso;
        }
        return S25Datefilter.transform(parsed, format) ?? startDateIso;
    }
}
